<script lang="ts">
    import { App } from "@/constants";
    import { GIFT_REGISTRY_WIDGET_NAMES } from "@/constants/widget-names";
    import { GiftRegistryPageWidgetDefinition } from "@apps/gift_registry/definitions";
    import { giftRegistryApplicationLoggerToken } from "@apps/gift_registry/tokens";
    import { ShareWishlistsQuery } from "@apps/wishlist/queries/ShareWishlistsQuery";
    import { WishlistProductStatusesQuery } from "@apps/wishlist/queries/WishlistProductStatusesQuery";
    import WishlistsBackInStockItemsQuery from "@apps/wishlist/queries/WishlistsBackInStockItemsQuery";
    import { WishlistsQuery } from "@apps/wishlist/queries/WishlistsQuery";
    import { ShareWishlistsService } from "@apps/wishlist/services/ShareWishlistsService";
    import WishlistsBackInStockItemsService from "@apps/wishlist/services/WishlistsBackInStockItemsService";
    import { WishlistsService } from "@apps/wishlist/services/WishlistsService";
    import WishlistsStorefrontValuesService from "@apps/wishlist/services/WishlistsStorefrontValuesService";
    import GrowaveConfigurationEntity from "@lib/GrowaveConfigurationProvider/GrowaveConfigurationEntity";
    import { AuthenticationInfoProviderInterface } from "@modules/authentication/AuthenticationInfoProvider/AuthenticationInfoProviderInterface";
    import ModulesStatusesProvider from "@modules/base_module/ModulesStatusesProvider/ModulesStatusesProvider";
    import { MODULE_STATUS } from "@modules/base_module/ModulesStatusesProvider/types";
    import { CartQuery } from "@modules/cart/queries/CartQuery";
    import { CartService } from "@modules/cart/services/CartService";
    import CartAvailabilityProvider from "@modules/common_module/CartAvailabilityProvider/CartAvailabilityProvider";
    import LocationControllerInterface from "@modules/common_module/LocationController/LocationControllerInterface";
    import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import { ExtractModuleType } from "@modules/dynamic_import/types";
    import GrowaveStorefrontValuesService from "@modules/growave_storefront_values/services/GrowaveStorefrontValuesService";
    import { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
    import { ProductsQuery } from "@modules/products/queries/ProductsQuery";
    import { ProductsWithQuantityRulesQuery } from "@modules/products/queries/ProductsWithQuantityRulesQuery";
    import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
    import { dispatchSdkEvent } from "@modules/sdk/helpers";
    import CN from "classnames";
    import { combineLatest, map, startWith } from "rxjs";
    import { onMount } from "svelte";
    import { container } from "tsyringe";

    import { ModalManager } from "@components/Modal/ModalManager";
    import Tabs from "@components/Tabs/Tabs.svelte";
    import TabsManager from "@components/Tabs/TabsManager";
    import TabsMenu from "@components/Tabs/TabsMenu.svelte";
    import TabsPanel from "@components/Tabs/TabsPanel.svelte";
    import { TabItem } from "@components/Tabs/types";
    import Widget from "@components/Widget.svelte";

    import WishlistPageWidgetContent from "./WishlistPageWidgetContent.svelte";

    export let wishlistsService: WishlistsService;
    export let wishlistsQuery: WishlistsQuery;
    export let wishlistsBackInStockItemsService: WishlistsBackInStockItemsService;
    export let wishlistsBackInStockItemsQuery: WishlistsBackInStockItemsQuery;
    export let wishlistProductStatusesQuery: WishlistProductStatusesQuery;
    export let wishlistsStorefrontValuesService: WishlistsStorefrontValuesService;
    export let productsQuery: ProductsQuery;
    export let productsWithQuantityRulesQuery: ProductsWithQuantityRulesQuery;
    export let cartService: CartService;
    export let cartQuery: CartQuery;
    export let modalManager: ModalManager;
    export let shareWishlistsService: ShareWishlistsService;
    export let shareWishlistsQuery: ShareWishlistsQuery;
    export let growaveStorefrontValuesService: GrowaveStorefrontValuesService;
    export let authenticationInfoProvider: AuthenticationInfoProviderInterface;
    export let dynamicImportManager: DynamicImportManager;
    export let cartAvailabilityProvider: CartAvailabilityProvider;
    export let afterWidgetInject: () => void;
    export let wishlistItemOptionsEditable: boolean;
    export let gwI18n: GwI18n;
    export let locationController: LocationControllerInterface;
    export let growaveConfiguration: GrowaveConfigurationEntity;
    export let modulesStatusesProvider: ModulesStatusesProvider;

    const isWishlistAppEnabled = growaveConfiguration.availableApps.includes(
        App.Wishlist
    );

    const isGiftRegistryAppEnabled =
        growaveConfiguration.availableApps.includes(App.GiftRegistry);

    const giftRegistryAppStatus$ = modulesStatusesProvider.selectModuleStatus(
        App.GiftRegistry
    );

    let GiftRegistryPageWidget:
        | ExtractModuleType<typeof GiftRegistryPageWidgetDefinition>["default"]
        | null = null;

    const importGiftRegistryPageWidget = async () => {
        if (GiftRegistryPageWidget) return;

        try {
            GiftRegistryPageWidget = await dynamicImportManager.importDefault(
                GiftRegistryPageWidgetDefinition,
                true
            );
        } catch (error) {
            console.error(
                "Failed to load GiftRegistryPageWidget module",
                error
            );
        }
    };

    $: if ($giftRegistryAppStatus$ === MODULE_STATUS.INITIALIZED) {
        importGiftRegistryPageWidget();
    }

    const handleGiftRegistryPageWidgetInit = () => {
        dispatchSdkEvent(
            new WidgetInjectedSdkEvent(
                GIFT_REGISTRY_WIDGET_NAMES.GIFT_REGISTRY_PAGE_WIDGET
            )
        );
    };

    const wishlists_tab_label$ = gwI18n.selectT(
        "wishlist.WishlistPageWidget",
        "wishlists_tab_label"
    );

    const registries_tab_label$ = gwI18n.selectT(
        "wishlist.WishlistPageWidget",
        "registries_tab_label"
    );

    const searchParams = locationController.getSearchParams();

    const tabsManager = new TabsManager();

    const tabs$ = combineLatest([
        wishlists_tab_label$,
        registries_tab_label$,
    ]).pipe(
        map(([wishlistsTabLabel, registriesTabLabel]) => {
            const tabs: TabItem[] = [];

            if (isWishlistAppEnabled) {
                tabs.push({
                    label: wishlistsTabLabel,
                    name: App.Wishlist,
                });
            }

            if (isGiftRegistryAppEnabled) {
                tabs.push({
                    label: registriesTabLabel,
                    name: App.GiftRegistry,
                });
            }

            return tabs;
        }),
        startWith([])
    );

    const isOnlyTab$ = tabs$.pipe(
        map((tabs) => {
            return tabs.length === 1;
        })
    );

    onMount(() => {
        const tabsSubscription = tabs$.subscribe((tabs) => {
            tabs.forEach((tab) => tabsManager.registerTab(tab.name));

            const tabParam = searchParams.get("tab");

            if (tabParam === App.GiftRegistry && isGiftRegistryAppEnabled) {
                tabsManager.openTab(App.GiftRegistry);
            }
        });

        afterWidgetInject();

        return () => {
            tabsSubscription.unsubscribe();
        };
    });
</script>

<div class="gw-wl-page__wrapper">
    <Tabs
        class={CN("gw-wl-page-widget-tabs", {
            "gw-wl-page-widget-tabs_is-only": $isOnlyTab$,
        })}
        tabsName="wl-page-widget-tabs"
    >
        <TabsMenu
            {tabsManager}
            tabs={$tabs$ || []}
            slot="menu"
            position="center"
        />
        <svelte:fragment slot="body">
            <TabsPanel {tabsManager} name={App.Wishlist}>
                {#if isWishlistAppEnabled}
                    <WishlistPageWidgetContent
                        showTitle={$isOnlyTab$}
                        {wishlistsService}
                        {wishlistsQuery}
                        {wishlistsBackInStockItemsService}
                        {wishlistsBackInStockItemsQuery}
                        {wishlistProductStatusesQuery}
                        {wishlistsStorefrontValuesService}
                        {productsQuery}
                        {productsWithQuantityRulesQuery}
                        {cartService}
                        {cartQuery}
                        {modalManager}
                        {shareWishlistsService}
                        {shareWishlistsQuery}
                        {authenticationInfoProvider}
                        {dynamicImportManager}
                        {growaveStorefrontValuesService}
                        {cartAvailabilityProvider}
                        {wishlistItemOptionsEditable}
                        {gwI18n}
                    />
                {/if}
            </TabsPanel>
            <TabsPanel {tabsManager} name={App.GiftRegistry}>
                {#if GiftRegistryPageWidget && $giftRegistryAppStatus$ === MODULE_STATUS.INITIALIZED}
                    <Widget
                        widget={GiftRegistryPageWidget}
                        params={[
                            {
                                showTitle: $isOnlyTab$,
                            },
                            container.resolve(
                                giftRegistryApplicationLoggerToken
                            ),
                        ]}
                        on:widgetInit={handleGiftRegistryPageWidgetInit}
                    />
                {/if}
            </TabsPanel>
        </svelte:fragment>
    </Tabs>
</div>

<style lang="postcss">
    .gw-wl-page__wrapper {
        --gw-tabs-underline-color: var(--gw-darkblue-border-color);

        margin-left: calc(-50vw + 50%);
        margin-right: calc(-50vw + 50%);
        padding: 0 40px;

        :global(.gw-tabs__hr) {
            display: none;
        }

        :global(.gw-tabs-panel) {
            padding: 2rem 0;
        }

        :global(.gw-wl-page-widget-tabs .gw-tabs-menu__list) {
            gap: 2em;
        }

        @media all and (max-width: 700px) {
            margin-right: 0;
            margin-left: 0;
            padding: 0 20px;
        }

        @media all and (max-width: 500px) {
            padding: 0;
        }
    }

    :global(
            .gw-wl-page-widget-tabs.gw-wl-page-widget-tabs_is-only
                .gw-tabs__header
        ) {
        display: none;
    }
</style>
